import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { 
  FileCheck, 
  Pen, 
  Eraser, 
  Check, 
  X,
  Shield,
  AlertTriangle,
  Download,
  MessageCircle
} from "lucide-react";

export default function GDPRConsent({ cliente, open, onOpenChange, onSuccess }) {
  const { token } = useAuth();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [consensi, setConsensi] = useState({
    trattamento: false,
    marketing: false,
    foto: false
  });

  // Testo informativa GDPR
  const informativa = `INFORMATIVA SULLA PRIVACY (Art. 13 Reg. UE 2016/679)

Il titolare del trattamento dei dati personali è I CIRILLO PARRUCCHIERI.

I dati personali forniti (nome, cognome, telefono, email, data di nascita) saranno trattati per:
• Gestione degli appuntamenti e dei servizi richiesti
• Comunicazioni relative ai servizi del salone
• Invio di promozioni e offerte (previo consenso specifico)

I dati saranno conservati per il tempo necessario alle finalità indicate e comunque non oltre 10 anni.

Il cliente ha diritto di accedere, rettificare, cancellare i propri dati e opporsi al trattamento.

Per esercitare i diritti, contattare: info@cirillo-parrucchieri.it`;

  // Setup canvas quando il dialog si apre
  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Imposta dimensioni canvas
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
      // Stile firma
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Sfondo bianco
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Linea guida per firma
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, rect.height - 30);
      ctx.lineTo(rect.width - 20, rect.height - 30);
      ctx.stroke();
      
      // Reset stile per firma
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 2;
    }
  }, [open]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      // Touch event
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const ctx = canvasRef.current.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Pulisci
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Ridisegna linea guida
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    
    // Reset stile
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    
    setHasSigned(false);
  };

  const handleSubmit = async () => {
    if (!consensi.trattamento) {
      toast.error("Il consenso al trattamento dati è obbligatorio");
      return;
    }
    
    if (!hasSigned) {
      toast.error("La firma è obbligatoria");
      return;
    }
    
    setLoading(true);
    
    try {
      // Converti canvas in base64
      const firma = canvasRef.current.toDataURL('image/png');
      
      await axios.post(
        `${API}/clienti/${cliente.id}/gdpr`,
        {
          cliente_id: cliente.id,
          cliente_nome: `${cliente.nome} ${cliente.cognome}`,
          consenso_trattamento: consensi.trattamento,
          consenso_marketing: consensi.marketing,
          consenso_foto: consensi.foto,
          firma_digitale: firma
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Consenso GDPR registrato con successo!");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio del consenso");
    } finally {
      setLoading(false);
    }
  };

  // Genera PDF del consenso GDPR
  const generatePDF = () => {
    if (!hasSigned || !consensi.trattamento) {
      toast.error("Devi prima firmare il consenso");
      return null;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    // Header con logo/titolo
    doc.setFillColor(30, 58, 95); // Blu scuro
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('I CIRILLO PARRUCCHIERI', pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Consenso Privacy GDPR', pageWidth / 2, 30, { align: 'center' });

    yPos = 55;
    doc.setTextColor(0, 0, 0);

    // Dati cliente
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DATI DEL CLIENTE', margin, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Nome e Cognome: ${cliente.nome} ${cliente.cognome}`, margin, yPos);
    yPos += 6;
    if (cliente.telefono) {
      doc.text(`Telefono: ${cliente.telefono}`, margin, yPos);
      yPos += 6;
    }
    if (cliente.email) {
      doc.text(`Email: ${cliente.email}`, margin, yPos);
      yPos += 6;
    }
    
    yPos += 10;

    // Informativa
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIVA SULLA PRIVACY', margin, yPos);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('(Art. 13 Reg. UE 2016/679 - GDPR)', margin + 75, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const informativaLines = [
      'Il titolare del trattamento dei dati personali è I CIRILLO PARRUCCHIERI.',
      '',
      'I dati personali forniti saranno trattati per:',
      '• Gestione degli appuntamenti e dei servizi richiesti',
      '• Comunicazioni relative ai servizi del salone',
      '• Invio di promozioni e offerte (previo consenso specifico)',
      '',
      'I dati saranno conservati per il tempo necessario alle finalità indicate',
      'e comunque non oltre 10 anni.',
      '',
      'Il cliente ha diritto di accedere, rettificare, cancellare i propri dati',
      'e opporsi al trattamento. Per esercitare i diritti: info@cirillo-parrucchieri.it'
    ];

    informativaLines.forEach(line => {
      doc.text(line, margin, yPos);
      yPos += 5;
    });

    yPos += 10;

    // Consensi
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CONSENSI ESPRESSI', margin, yPos);
    yPos += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    // Checkbox style
    const drawCheckbox = (x, y, checked) => {
      doc.setDrawColor(100, 100, 100);
      doc.rect(x, y - 4, 5, 5);
      if (checked) {
        doc.setDrawColor(0, 128, 0);
        doc.setLineWidth(0.5);
        doc.line(x + 1, y - 1.5, x + 2, y);
        doc.line(x + 2, y, x + 4, y - 3);
        doc.setLineWidth(0.2);
      }
    };

    drawCheckbox(margin, yPos, consensi.trattamento);
    doc.text('Consenso al trattamento dei dati personali (OBBLIGATORIO)', margin + 8, yPos);
    yPos += 8;

    drawCheckbox(margin, yPos, consensi.marketing);
    doc.text('Consenso per comunicazioni promozionali', margin + 8, yPos);
    yPos += 8;

    drawCheckbox(margin, yPos, consensi.foto);
    doc.text('Consenso per pubblicazione foto sui social/portfolio', margin + 8, yPos);
    yPos += 15;

    // Data e firma
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FIRMA DEL CLIENTE', margin, yPos);
    yPos += 8;

    const dataFirma = new Date().toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Data: ${dataFirma}`, margin, yPos);
    yPos += 10;

    // Inserisci firma dal canvas
    if (canvasRef.current) {
      const firmaImg = canvasRef.current.toDataURL('image/png');
      doc.addImage(firmaImg, 'PNG', margin, yPos, 60, 30);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      'Documento generato digitalmente - I CIRILLO PARRUCCHIERI',
      pageWidth / 2,
      285,
      { align: 'center' }
    );

    return doc;
  };

  // Scarica PDF
  const handleDownloadPDF = () => {
    const doc = generatePDF();
    if (doc) {
      const fileName = `GDPR_${cliente.nome}_${cliente.cognome}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success("PDF scaricato!");
    }
  };

  // Invia via WhatsApp
  const handleSendWhatsApp = () => {
    const doc = generatePDF();
    if (!doc) return;

    // Prima scarica il PDF
    const fileName = `GDPR_${cliente.nome}_${cliente.cognome}.pdf`;
    doc.save(fileName);

    // Formatta numero telefono per WhatsApp (rimuovi spazi e + iniziale se c'è)
    let telefono = cliente.telefono || '';
    telefono = telefono.replace(/\s+/g, '').replace(/^\+/, '');
    
    // Se inizia con 0, sostituisci con 39 (Italia)
    if (telefono.startsWith('0')) {
      telefono = '39' + telefono.substring(1);
    }
    // Se non ha prefisso internazionale, aggiungi 39
    if (telefono.length <= 10) {
      telefono = '39' + telefono;
    }

    const messaggio = encodeURIComponent(
      `Gentile ${cliente.nome},\n\n` +
      `In allegato trova il documento di consenso privacy (GDPR) che ha appena firmato presso I CIRILLO PARRUCCHIERI.\n\n` +
      `Conservi questo documento per i suoi archivi.\n\n` +
      `Grazie per la fiducia!\n` +
      `I CIRILLO PARRUCCHIERI`
    );

    // Apri WhatsApp con il numero
    const whatsappUrl = `https://wa.me/${telefono}?text=${messaggio}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success("WhatsApp aperto! Allega il PDF appena scaricato.", {
      duration: 5000
    });
  };

  if (!cliente) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="gdpr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Shield className="w-6 h-6 text-blue-600" />
            Consenso Privacy GDPR
          </DialogTitle>
          <p className="text-sm text-slate-500">
            {cliente.nome} {cliente.cognome}
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informativa */}
          <div className="bg-slate-50 rounded-lg p-4 max-h-48 overflow-y-auto">
            <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              Informativa sulla Privacy
            </h3>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">
              {informativa}
            </pre>
          </div>

          {/* Consensi */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800">Consensi</h3>
            
            {/* Consenso Trattamento (obbligatorio) */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Checkbox 
                id="trattamento"
                checked={consensi.trattamento}
                onCheckedChange={(checked) => setConsensi({...consensi, trattamento: checked})}
                className="mt-0.5"
                data-testid="gdpr-checkbox-trattamento"
              />
              <div className="flex-1">
                <Label htmlFor="trattamento" className="font-medium text-blue-800 cursor-pointer">
                  Consenso al trattamento dei dati personali *
                </Label>
                <p className="text-xs text-blue-600 mt-1">
                  Obbligatorio per usufruire dei servizi del salone
                </p>
              </div>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>

            {/* Consenso Marketing (opzionale) */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <Checkbox 
                id="marketing"
                checked={consensi.marketing}
                onCheckedChange={(checked) => setConsensi({...consensi, marketing: checked})}
                className="mt-0.5"
                data-testid="gdpr-checkbox-marketing"
              />
              <div className="flex-1">
                <Label htmlFor="marketing" className="font-medium text-slate-800 cursor-pointer">
                  Consenso per comunicazioni promozionali
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Riceverai offerte esclusive e promozioni via SMS/email
                </p>
              </div>
            </div>

            {/* Consenso Foto (opzionale) */}
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <Checkbox 
                id="foto"
                checked={consensi.foto}
                onCheckedChange={(checked) => setConsensi({...consensi, foto: checked})}
                className="mt-0.5"
                data-testid="gdpr-checkbox-foto"
              />
              <div className="flex-1">
                <Label htmlFor="foto" className="font-medium text-slate-800 cursor-pointer">
                  Consenso per pubblicazione foto
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Autorizzo la pubblicazione di foto dei miei trattamenti sui social/portfolio
                </p>
              </div>
            </div>
          </div>

          {/* Area Firma */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Pen className="w-4 h-4" />
                Firma del Cliente
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="gap-1"
                data-testid="gdpr-clear-signature"
              >
                <Eraser className="w-4 h-4" />
                Cancella
              </Button>
            </div>
            
            <div className="relative border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden">
              <canvas
                ref={canvasRef}
                className="w-full touch-none cursor-crosshair"
                style={{ height: '150px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="gdpr-signature-canvas"
              />
              
              {!hasSigned && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-slate-400 text-sm">
                    Firma qui con il dito o il mouse
                  </p>
                </div>
              )}
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Data: {new Date().toLocaleDateString('it-IT', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {/* Azioni */}
          <div className="space-y-3 pt-4 border-t">
            {/* Prima riga: Annulla e Conferma */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
                disabled={loading}
                data-testid="gdpr-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!consensi.trattamento || !hasSigned || loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="gdpr-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Conferma e Firma
                  </>
                )}
              </Button>
            </div>
            
            {/* Seconda riga: PDF e WhatsApp (visibili solo se firmato) */}
            {hasSigned && consensi.trattamento && (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleDownloadPDF}
                  className="flex-1 border-slate-300 hover:bg-slate-50"
                  data-testid="gdpr-download-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica PDF
                </Button>
                <Button
                  onClick={handleSendWhatsApp}
                  disabled={!cliente.telefono}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="gdpr-send-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Invia WhatsApp
                </Button>
              </div>
            )}
            
            {hasSigned && consensi.trattamento && !cliente.telefono && (
              <p className="text-xs text-amber-600 text-center">
                Per inviare via WhatsApp, aggiungi il numero di telefono al cliente
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
